import { conform, type Submission, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { invariant } from '@epic-web/invariant'
import {
	type DataFunctionArgs,
	json,
	type MetaFunction,
	redirect,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	AuthAlt,
	AuthFooter,
	CodeBoxes,
	QuoteCaption,
	SubmitButton,
} from '#app/components/auth/auth-ui.tsx'
import ResetPassword from '#app/components/emails/reset-password.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { AuthShell } from '#app/components/layout/auth-shell.tsx'
import { FormError } from '#app/components/ui/field.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { WEDDING } from '#app/config/wedding.ts'
import {
	handleChangeEmailVerification,
	twoFAVerificationType,
	type twoFAVerifyVerificationType,
} from '#app/routes/user+/__account.server.ts'
import { onboardingSessionKey, requireUserId } from '#app/utils/auth.server.ts'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { ensurePrimary } from '#app/utils/litefs.server.ts'
import { getDomainUrl, useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { generateTOTP, verifyTOTP } from '#app/utils/totp.server.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import {
	handleVerification as handleLoginTwoFactorVerification,
	shouldRequestTwoFA,
} from './login.tsx'
import { handleVerification as handleResetPasswordVerification } from './reset-password.tsx'

export const codeQueryParam = 'code'
export const targetQueryParam = 'target'
export const typeQueryParam = 'type'
export const redirectToQueryParam = 'redirectTo'
const types = ['onboarding', 'reset-password', 'change-email', '2fa'] as const
const VerificationTypeSchema = z.enum(types)
export type VerificationTypes = z.infer<typeof VerificationTypeSchema>

const VerifySchema = z.object({
	[codeQueryParam]: z.string().min(6).max(6),
	[typeQueryParam]: VerificationTypeSchema,
	[targetQueryParam]: z.string(),
	[redirectToQueryParam]: z.string().optional(),
})

export const meta: MetaFunction = () => {
	return [{ title: `Verify — ${WEDDING.title}` }]
}

export async function handleVerification({ submission }: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set(onboardingSessionKey, submission.value.target)
	return redirect('/onboarding', {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	await validateCSRF(formData, request.headers)
	if (formData.get('intent') === 'resend') return resendResetCode(request, formData)
	return validateRequest(request, formData)
}

/** Re-sends a password-reset code. Responds the same whether or not the account exists. */
async function resendResetCode(request: Request, formData: FormData) {
	const target = String(formData.get(targetQueryParam) ?? '').toLowerCase()
	if (formData.get(typeQueryParam) !== 'reset-password' || !target) {
		return json({ resent: false } as const, { status: 400 })
	}
	const user = await prisma.user.findUnique({
		where: { email: target },
		select: { email: true, name: true },
	})
	if (user) {
		const { verifyUrl, otp } = await prepareVerification({
			period: 10 * 60,
			request,
			type: 'reset-password',
			target,
		})
		await sendEmail({
			to: user.email,
			subject: `${WEDDING.title} — password reset`,
			react: <ResetPassword onboardingUrl={verifyUrl.toString()} name={user.name} otp={otp} />,
		})
	}
	return json({ resent: true } as const)
}

export function getRedirectToUrl({
	request,
	type,
	target,
	redirectTo,
}: {
	request: Request
	type: VerificationTypes
	target: string
	redirectTo?: string
}) {
	const redirectToUrl = new URL(`${getDomainUrl(request)}/verify`)
	redirectToUrl.searchParams.set(typeQueryParam, type)
	redirectToUrl.searchParams.set(targetQueryParam, target)
	if (redirectTo) {
		redirectToUrl.searchParams.set(redirectToQueryParam, redirectTo)
	}
	return redirectToUrl
}

export async function requireRecentVerification(request: Request) {
	const userId = await requireUserId(request)
	const shouldReverify = await shouldRequestTwoFA(request)
	if (shouldReverify) {
		const reqUrl = new URL(request.url)
		const redirectUrl = getRedirectToUrl({
			request,
			target: userId,
			type: twoFAVerificationType,
			redirectTo: reqUrl.pathname + reqUrl.search,
		})
		throw await redirectWithToast(redirectUrl.toString(), {
			title: 'Please Reverify',
			description: 'Please reverify your account before proceeding',
		})
	}
}

export async function prepareVerification({
	period,
	request,
	type,
	target,
}: {
	period: number
	request: Request
	type: VerificationTypes
	target: string
}) {
	const verifyUrl = getRedirectToUrl({ request, type, target })
	const redirectTo = new URL(verifyUrl.toString())

	const { otp, ...verificationConfig } = generateTOTP({
		algorithm: 'SHA256',
		// Leaving off 0 and O on purpose to avoid confusing users.
		charSet: 'ABCDEFGHIJKLMNPQRSTUVWXYZ123456789',
		period,
	})
	const verificationData = {
		type,
		target,
		...verificationConfig,
		expiresAt: new Date(Date.now() + verificationConfig.period * 1000),
	}
	await prisma.verification.upsert({
		where: { target_type: { target, type } },
		create: verificationData,
		update: verificationData,
	})

	// add the otp to the url we'll email the user+.
	verifyUrl.searchParams.set(codeQueryParam, otp)

	return { otp, redirectTo, verifyUrl }
}

export type VerifyFunctionArgs = {
	request: Request
	submission: Submission<z.infer<typeof VerifySchema>>
	body: FormData | URLSearchParams
}

export async function isCodeValid({
	code,
	type,
	target,
}: {
	code: string
	type: VerificationTypes | typeof twoFAVerifyVerificationType
	target: string
}) {
	const verification = await prisma.verification.findUnique({
		where: {
			target_type: { target, type },
			OR: [{ expiresAt: { gt: new Date() } }, { expiresAt: null }],
		},
		select: { algorithm: true, secret: true, period: true, charSet: true },
	})
	if (!verification) return false
	return verifyTOTP({
		otp: code,
		...verification,
	})
}

async function validateRequest(
	request: Request,
	body: URLSearchParams | FormData,
) {
	const submission = await parse(body, {
		schema: VerifySchema.superRefine(async (data, ctx) => {
			const codeIsValid = await isCodeValid({
				code: data[codeQueryParam],
				type: data[typeQueryParam],
				target: data[targetQueryParam],
			})
			if (!codeIsValid) {
				ctx.addIssue({
					path: ['code'],
					code: z.ZodIssueCode.custom,
					message: `Invalid code`,
				})
				return
			}
		}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	// this code path could be part of a loader (GET request), so we need to make
	// sure we're running on primary because we're about to make writes.
	await ensurePrimary()

	const { value: submissionValue } = submission

	async function deleteVerification() {
		await prisma.verification.delete({
			where: {
				target_type: {
					type: submissionValue[typeQueryParam],
					target: submissionValue[targetQueryParam],
				},
			},
		})
	}

	switch (submissionValue[typeQueryParam]) {
		case 'reset-password': {
			await deleteVerification()
			return handleResetPasswordVerification({ request, body, submission })
		}
		case 'onboarding': {
			await deleteVerification()
			return handleVerification({ request, body, submission })
		}
		case 'change-email': {
			await deleteVerification()
			return handleChangeEmailVerification({ request, body, submission })
		}
		case '2fa': {
			return handleLoginTwoFactorVerification({ request, body, submission })
		}
	}
}

export default function VerifyRoute() {
	const [searchParams] = useSearchParams()
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const parsedType = VerificationTypeSchema.safeParse(searchParams.get(typeQueryParam))
	const type = parsedType.success ? parsedType.data : null
	const target = searchParams.get(targetQueryParam) ?? ''
	const resent = actionData && 'resent' in actionData

	const [form, fields] = useForm({
		id: 'verify-form',
		constraint: getFieldsetConstraint(VerifySchema),
		lastSubmission: actionData && 'submission' in actionData ? actionData.submission : undefined,
		onValidate({ formData }) {
			return parse(formData, { schema: VerifySchema })
		},
		defaultValue: {
			code: searchParams.get(codeQueryParam) ?? '',
			type,
			target,
			redirectTo: searchParams.get(redirectToQueryParam) ?? '',
		},
	})

	const isTwoFA = type === '2fa'
	const codeError =
		fields[codeQueryParam].errors?.[0] === 'Invalid code'
			? 'That code didn’t work. Check it and try again.'
			: fields[codeQueryParam].errors?.[0]
				? 'Enter the 6-character code'
				: undefined

	return (
		<AuthShell
			image="/img/demo/couple/savanna-walk.webp"
			imageAlt={`${WEDDING.couple.names.join(' and ')} walking through tall grass`}
			caption={<QuoteCaption quote={`“${WEDDING.scripture.text}”`} cite={WEDDING.scripture.cite} />}
			topRight={<Link to="/login" className="link">Sign in</Link>}
			footer={<AuthFooter />}
		>
			<p className="mono text-stone">{isTwoFA ? 'Two-factor authentication' : 'Verification'}</p>
			<h1 className="mt-4 font-serif text-[60px] leading-[.95] tracking-[-.01em]">
				{isTwoFA ? (
					<>
						Check your <em className="italic text-brown">app</em>
					</>
				) : (
					<>
						Check your <em className="italic text-brown">email</em>
					</>
				)}
			</h1>
			<p className="mt-3 text-base text-stone">
				{!type
					? 'This verification link isn’t valid.'
					: isTwoFA
						? 'Enter the 6-digit code from your authenticator app.'
						: target.includes('@')
							? <>We sent a 6-character code to <span className="text-ink">{target}</span>.</>
							: 'We sent you a 6-character code.'}
			</p>

			<Form method="POST" {...form.props} className="mt-7 flex flex-col gap-5">
				<AuthenticityTokenInput />
				<HoneypotInputs />
				<div className="flex flex-col gap-2">
					<Label>Code</Label>
					<CodeBoxes
						name={fields[codeQueryParam].name}
						defaultValue={searchParams.get(codeQueryParam) ?? ''}
						invalid={Boolean(codeError)}
						autoFocus
					/>
					{codeError ? <p className="text-[13px] text-bad">{codeError}</p> : null}
				</div>
				<input {...conform.input(fields[typeQueryParam], { type: 'hidden' })} />
				<input {...conform.input(fields[targetQueryParam], { type: 'hidden' })} />
				<input {...conform.input(fields[redirectToQueryParam], { type: 'hidden' })} />
				<FormError>{form.errors?.[0]}</FormError>
				<SubmitButton pending={isPending}>Verify</SubmitButton>
			</Form>

			{type === 'reset-password' ? (
				<Form method="POST" className="mt-7 border-t border-line pt-6 text-sm text-stone">
					<AuthenticityTokenInput />
					<HoneypotInputs />
					<input type="hidden" name="intent" value="resend" />
					<input type="hidden" name={typeQueryParam} value={type} />
					<input type="hidden" name={targetQueryParam} value={target} />
					{resent ? (
						<span>A new code is on its way.</span>
					) : (
						<>
							Didn’t get it?{' '}
							<button type="submit" className="border-b border-current pb-0.5 text-ink hover:text-brown">
								Resend code
							</button>
						</>
					)}
				</Form>
			) : (
				<AuthAlt>
					Wrong account? <Link to="/login">Back to sign in</Link>
				</AuthAlt>
			)}
		</AuthShell>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
